import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type SnackbarKind = 'default' | 'api-error' | 'success';

export interface SnackbarEntry {
  id: string;
  text: string;
  kind: SnackbarKind;
  timestamp: number;
  code?: number;
  method?: string;
  apiLabel?: string;
  /** Raw BE response body (JSON or HTML) for the debug copy panel. */
  detail?: string;
}

export interface ShowSnackbarPayload {
  text: string;
  /** 자동 닫힘 시간(ms). 생략 시 수동으로만 닫음 */
  duration?: number;
  kind?: SnackbarKind;
  code?: number;
  method?: string;
  apiLabel?: string;
  timestamp?: number;
  detail?: string;
}

export type ShowSnackbarArg = string | ShowSnackbarPayload;

interface SnackbarState {
  open: boolean;
  current: SnackbarEntry | null;
  history: SnackbarEntry[];
  duration: number;
  expanded: boolean;
}

/**
 * Accumulate up to this many recent api-error entries in history before
 * dropping the oldest. The list persists until the user explicitly clears
 * it via the ErrorBadge debug panel.
 */
const MAX_API_ERROR_HISTORY = 200;

const initialState: SnackbarState = {
  open: false,
  current: null,
  history: [],
  duration: 0,
  expanded: false,
};

function toSnackbarEntry(payload: ShowSnackbarArg): { entry: SnackbarEntry; duration: number } {
  if (typeof payload === 'string') {
    return {
      entry: {
        id: crypto.randomUUID(),
        text: payload,
        kind: 'default',
        timestamp: Date.now(),
      },
      duration: 0,
    };
  }

  return {
    entry: {
      id: crypto.randomUUID(),
      text: payload.text,
      kind: payload.kind ?? 'default',
      timestamp: payload.timestamp ?? Date.now(),
      code: payload.code,
      method: payload.method,
      apiLabel: payload.apiLabel,
      detail: payload.detail,
    },
    duration: payload.duration ?? 0,
  };
}

function pushApiErrorHistory(current: SnackbarEntry | null, history: SnackbarEntry[]): SnackbarEntry[] {
  if (current?.kind !== 'api-error') return history;
  return [current, ...history].slice(0, MAX_API_ERROR_HISTORY);
}

const snackbarSlice = createSlice({
  name: 'snackbar',
  initialState,
  reducers: {
    show: (state, action: PayloadAction<ShowSnackbarArg>) => {
      const { entry, duration } = toSnackbarEntry(action.payload);
      state.history = pushApiErrorHistory(state.current, state.history);
      state.open = true;
      state.current = entry;
      state.duration = duration;
      state.expanded = false;
    },
    /**
     * Record an api-error in history without showing the floating toast.
     * Use for pages that render their own full-screen error UI but still
     * want the error to appear in the ErrorBadge debug panel.
     */
    recordSilent: (state, action: PayloadAction<ShowSnackbarArg>) => {
      const { entry } = toSnackbarEntry(action.payload);
      if (entry.kind !== 'api-error') return;
      state.history = [entry, ...state.history].slice(0, MAX_API_ERROR_HISTORY);
    },
    hide: (state) => {
      state.open = false;
      state.expanded = false;
    },
    toggleExpanded: (state) => {
      if (state.history.length === 0) return;
      state.expanded = !state.expanded;
    },
    clearHistory: (state) => {
      state.history = [];
      state.expanded = false;
      // Phase 21: also dismiss any currently-visible api-error so ErrorBadge clears fully.
      // (Without this, "전체 비우기" left the last error in `current` and the badge stayed.)
      if (state.current?.kind === 'api-error') {
        state.current = null;
        state.open = false;
      }
    },
  },
});

export const {
  show: showSnackbar,
  recordSilent: recordSnackbarSilently,
  hide: hideSnackbar,
  toggleExpanded,
  clearHistory,
} = snackbarSlice.actions;
export default snackbarSlice.reducer;
